import type {
  ActiveGameData,
  ActiveGameDataCash,
  ActiveGameDataSAG,
  ActiveGameDataTourney
} from '@ps/web-poker-types';
import { ActiveGameButtonType, ActiveGameType } from '@ps/web-poker-types';
import { formatCurrency, isSpingo } from '@ps/web-poker-utils';
import type {
  TableData,
  TournDataStruct,
  ZoomDataStruct
} from '@ps/web-shared-generated-graphql/AG';

import { formatGame } from './formatters/game';

const mapMttType = (
  data?: TournDataStruct | null
): ActiveGameDataTourney | ActiveGameDataSAG | undefined => {
  if (!data?.staticData) return undefined;

  if (isSpingo(data.staticData.tournFlags2)) {
    const gameVariant = formatGame(
      data.staticData.game,
      data.staticData.structure,
      Boolean(data.staticData.isHiLo),
      true
    );

    const buyIn = formatCurrency(
      data.staticData.buyIn + data.staticData.rake,
      `${data.staticData.currency}`,
      {
        separators: true,
        isPlayMoney: Number(data.staticData.isPlayMoney) || 0,
        scalePM: data.staticData.scalePm
      }
    );

    return {
      id: data?.staticData.tournamentId,
      buttonType: ActiveGameButtonType.TAKE_SEAT,
      type: ActiveGameType.SAG,
      gameVariant,
      buyIn,
      name: data?.staticData.name?.replace(/(<([^>]+)>)/gi, '') || ''
    };
  }

  return {
    id: data?.staticData.tournamentId,
    buttonType: ActiveGameButtonType.UNREGISTER,
    type: ActiveGameType.Tourney,
    name: data?.staticData.name?.replace(/(<([^>]+)>)/gi, '') || '',
    startTime: data?.staticData.whenStartInt || 0
  };
};

const mapTableType = (data?: TableData | null): ActiveGameDataCash | undefined => {
  if (!data?.staticData) return undefined;

  return {
    id: data.staticData.tableId,
    name: data.staticData.name || '',
    gameFormat: formatGame(
      data.staticData.game,
      data.staticData.structure,
      Boolean(data.staticData.isHiLo),
      false
    ),
    type: ActiveGameType.Cash,
    buttonType: ActiveGameButtonType.TAKE_SEAT
  } as ActiveGameDataCash;
};

const mapZoomType = (data?: ZoomDataStruct | null): ActiveGameDataCash | undefined => {
  if (!data?.staticData) return undefined;

  return {
    id: data.staticData.id,
    name: data.staticData.name || '',
    gameFormat: formatGame(
      data.staticData.game,
      data.staticData.structure,
      Boolean(data.staticData.isHiLo),
      false
    ),
    type: ActiveGameType.Zoom,
    buttonType: ActiveGameButtonType.TAKE_SEAT
  } as ActiveGameDataCash;
};

const mapActiveGameTables = (data?: (TableData | null)[]): ActiveGameDataCash[] => {
  if (!data) return [];

  return data.map((item) => mapTableType(item)).filter((item) => !!item) as ActiveGameDataCash[];
};

const mapActiveGameZoomTypes = (data?: (ZoomDataStruct | null)[]): ActiveGameDataCash[] => {
  if (!data) return [];

  return data.map((item) => mapZoomType(item)).filter((item) => !!item) as ActiveGameDataCash[];
};

const mapActiveGameMttTypes = (data?: (TournDataStruct | null)[]): ActiveGameDataTourney[] => {
  if (!data) return [];

  return data.map((item) => mapMttType(item)).filter((item) => !!item) as ActiveGameDataTourney[];
};

const getActiveGameTableIds = (data?: (TableData | null)[]): string[] => {
  if (!data) return [];

  return data.map((item) => `${item?.staticData?.tableId}`).filter((item) => !!item);
};

const getActiveGameZoomIds = (data?: (ZoomDataStruct | null)[]): string[] => {
  if (!data) return [];

  return data.map((item) => `${item?.staticData?.id}`).filter((item) => !!item);
};

const getActiveGameTournamentIds = (data?: (TournDataStruct | null)[]): string[] => {
  if (!data) return [];

  return data.map((item) => `${item?.staticData?.tournamentId}`).filter((item) => !!item);
};

export const getActiveGameIds = (data?: {
  tables?: (TableData | null)[];
  zoomTypes?: (ZoomDataStruct | null)[];
  tournaments?: (TournDataStruct | null)[];
}): string[] => {
  if (!data) return [];

  return [
    ...getActiveGameTableIds(data.tables),
    ...getActiveGameZoomIds(data.zoomTypes),
    ...getActiveGameTournamentIds(data.tournaments)
  ].filter((item) => !!item);
};

export const mapActiveGames = (data?: {
  tables?: (TableData | null)[];
  zoomTypes?: (ZoomDataStruct | null)[];
  tournaments?: (TournDataStruct | null)[];
}): ActiveGameData[] => {
  if (!data) return [];

  return [
    ...mapActiveGameTables(data.tables),
    ...mapActiveGameZoomTypes(data.zoomTypes),
    ...mapActiveGameMttTypes(data.tournaments)
  ];
};
